import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Plus, Minus, Trash2, MapPin, AlertCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { fmtEUR, api } from "../lib/api";
import { primaryImg } from "../lib/img";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useState, useEffect, useCallback } from "react";

const COUNTRY_LABELS = { LU: "Luxembourg", FR: "France", BE: "Belgique" };

export default function CartSheet() {
  const { items, open, setOpen, update, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingPP, setLoadingPP] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [billing, setBilling] = useState(null);
  const [selectedAddrId, setSelectedAddrId] = useState("");

  const refreshAddresses = useCallback(async () => {
    if (!user) { setAddresses([]); setBilling(null); return; }
    try {
      const [r1, r2] = await Promise.all([api.get("/addresses"), api.get("/billing-address")]);
      setAddresses(r1.data);
      setBilling(r2.data);
      const def = r1.data.find((a) => a.is_default) || r1.data[0];
      if (def) setSelectedAddrId(def.id);
    } catch (err) {
      // anonymous user (401) is the common case — don't surface to UI
      if (err?.response?.status !== 401) console.debug("[CartSheet] addresses load failed:", err);
    }
  }, [user]);

  useEffect(() => { if (open) refreshAddresses(); }, [open, refreshAddresses]);

  const handleCheckout = async () => {
    if (!user) {
      setOpen(false);
      navigate("/auth");
      toast.message("Connectez-vous pour finaliser votre commande");
      return;
    }
    if (!selectedAddrId) {
      toast.error("Veuillez ajouter ou sélectionner une adresse de livraison");
      return;
    }
    setLoading(true);
    try {
      const origin_url = window.location.origin;
      const r = await api.post("/checkout/create-session", { origin_url, shipping_address_id: selectedAddrId });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur paiement");
      setLoading(false);
    }
  };

  const handlePayPal = async () => {
    if (!user) {
      setOpen(false);
      navigate("/auth");
      toast.message("Connectez-vous pour finaliser votre commande");
      return;
    }
    if (!selectedAddrId) {
      toast.error("Veuillez ajouter ou sélectionner une adresse de livraison");
      return;
    }
    if (!billing) {
      toast.error("Veuillez ajouter votre adresse de facturation");
      goToAddresses();
      return;
    }
    setLoadingPP(true);
    try {
      const origin_url = window.location.origin;
      const r = await api.post("/checkout/paypal/create-order", { origin_url, shipping_address_id: selectedAddrId });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur PayPal");
      setLoadingPP(false);
    }
  };

  const goToAddresses = () => {
    setOpen(false);
    navigate("/mes-adresses");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-sheet">
        <SheetHeader>
          <SheetTitle className="font-black text-2xl tracking-tight">Votre panier</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6 space-y-4">
          {items.length === 0 && (
            <p className="text-[#4B634B] text-center mt-12" data-testid="cart-empty">Votre panier est vide.</p>
          )}
          {items.map((it) => {
            const p = it.product;
            const unit = p.is_promo && p.promo_price ? p.promo_price : p.price;
            return (
              <div key={p.id} className="flex gap-3 border border-[#E5EAE5] rounded-2xl p-3" data-testid={`cart-item-${p.id}`}>
                <img src={primaryImg(p)} alt={p.name} className="w-20 h-20 rounded-xl object-cover" />
                <div className="flex-1">
                  <div className="font-semibold text-sm leading-tight">{p.name}</div>
                  <div className="text-[#7AC143] font-bold mt-1">{fmtEUR(unit)}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => update(p.id, it.quantity - 1)} className="w-7 h-7 rounded-full border border-[#E5EAE5] flex items-center justify-center hover:bg-[#F2F5F0]" data-testid={`cart-decrement-${p.id}`}>
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="text-sm font-semibold w-6 text-center">{it.quantity}</span>
                    <button onClick={() => update(p.id, it.quantity + 1)} className="w-7 h-7 rounded-full border border-[#E5EAE5] flex items-center justify-center hover:bg-[#F2F5F0]" data-testid={`cart-increment-${p.id}`}>
                      <Plus className="w-3 h-3" />
                    </button>
                    <button onClick={() => update(p.id, 0)} className="ml-auto text-[#4B634B] hover:text-red-600" data-testid={`cart-remove-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#E5EAE5] pt-4 space-y-3">
            <div className="flex justify-between font-semibold">
              <span>Total</span>
              <span data-testid="cart-total" className="text-[#7AC143] text-lg">{fmtEUR(total)}</span>
            </div>

            {user && (
              <div className="space-y-2">
                {!billing && (
                  <button onClick={goToAddresses} data-testid="cart-add-billing-btn" className="w-full flex items-center gap-3 p-3 border border-dashed border-[#FACC15] bg-[#FACC15]/10 rounded-xl text-left">
                    <AlertCircle className="w-5 h-5 text-[#FACC15] shrink-0" />
                    <span className="text-sm font-semibold text-[#1A2E1A]">Ajouter votre adresse de facturation (obligatoire)</span>
                  </button>
                )}
                {addresses.length === 0 ? (
                  <button onClick={goToAddresses} data-testid="cart-add-address-btn" className="w-full flex items-center gap-3 p-3 border border-dashed border-[#FACC15] bg-[#FACC15]/10 rounded-xl text-left">
                    <AlertCircle className="w-5 h-5 text-[#FACC15] shrink-0" />
                    <span className="text-sm font-semibold text-[#1A2E1A]">Ajouter une adresse de livraison</span>
                  </button>
                ) : (
                  <div className="border border-[#E5EAE5] rounded-xl p-3" data-testid="cart-address-section">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#4B634B] mb-2">
                      <MapPin className="w-3.5 h-3.5" /> Livraison
                    </div>
                    <Select value={selectedAddrId} onValueChange={setSelectedAddrId}>
                      <SelectTrigger data-testid="cart-address-select" className="w-full text-left">
                        <SelectValue placeholder="Choisir une adresse" />
                      </SelectTrigger>
                      <SelectContent>
                        {addresses.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.full_name} — {a.postal_code} {a.city}, {COUNTRY_LABELS[a.country]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button onClick={goToAddresses} className="text-xs text-[#7AC143] hover:underline mt-2" data-testid="cart-manage-addresses">
                      Gérer mes adresses
                    </button>
                  </div>
                )}
              </div>
            )}

            <Button
              onClick={handleCheckout}
              disabled={loading || loadingPP}
              data-testid="cart-checkout-btn"
              className="w-full bg-[#7AC143] hover:bg-[#67BC2A] text-white rounded-full py-6 font-bold"
            >
              {loading ? "Redirection..." : "Payer par carte (Stripe)"}
            </Button>
            <Button
              onClick={handlePayPal}
              disabled={loading || loadingPP}
              data-testid="cart-paypal-btn"
              variant="outline"
              className="w-full border-2 border-[#003087] text-[#003087] hover:bg-[#003087] hover:text-white rounded-full py-6 font-bold"
            >
              {loadingPP ? "Redirection..." : "Payer avec PayPal"}
            </Button>
            <p className="text-xs text-[#4B634B] text-center">Paiement sécurisé · Livraison 🇱🇺 🇫🇷 🇧🇪</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
